import axios from "axios";
import {
    Keyword,
    KeywordsDTO,
    SimpleKeyword,
    SimpleKeywordSchema,
    TKeywordsList,
    UpsertKeywordOrderText,
    upsertKeywordDTO,
} from "src/app/types/keywords";
import { toCamelCase, toSnakeCase } from "../utils/objectHelper";
import qs from "qs";
import { Order, OrderSchema, TContentType } from "../types/orders/order";
import { TLangType } from "../types/LangType";

export const KeywordsTransport = {
    list: async (keywordType: TKeywordsList, filters?: any): Promise<SimpleKeyword[]> => {
        const { data } = await axios.get<SimpleKeyword[]>(
            filters
                ? `/keywords/?keywordType=${keywordType}&${qs.stringify(filters)}`
                : `/keywords/?keywordType=${keywordType}`
        );
        return data?.map((keyword) =>
            SimpleKeywordSchema.cast(toCamelCase(keyword), { stripUnknown: true })
        );
    },
    find: async (id?: number): Promise<Keyword> => {
        const { data } = await axios.get<Keyword>(`/keyword/${id}`);
        return toCamelCase(data);
    },
    toggleActive: async (keyword: Keyword): Promise<void> => {
        const action = "toggleActive";
        await axios.patch<Keyword>(`/keywords/${keyword.id}/actions/${action}`);
    },
    verify: async (keyword: upsertKeywordDTO): Promise<void> => {
        const action = "verify";
        await axios.request({
            method: "put",
            url: `/keyword/${keyword?.id}/actions/${action}`,
            data: toCamelCase(keyword),
        });
    },
    checkVolumeInSemrush: async (keywords: Array<string>): Promise<any> => {
        const { data } = await axios.get<any>(
            `/keywords/checkVolumeInSemrush?keywords=${keywords.join(",")}`
        );
        return data;
    },

    totalFiltered: async (keywordType: TKeywordsList, filters?: any): Promise<number> => {
        const { data } = await axios.get<number>(
            filters
                ? `/keywords/totalFiltered?keywordType=${keywordType}&${qs.stringify(filters)}`
                : `/keywords/totalFiltered?keywordType=${keywordType}`
        );

        return data;
    },
    total: async (keywordType: TKeywordsList): Promise<number> => {
        const { data } = await axios.get<number>(`/keywords/total?keywordType=${keywordType}`);

        return data;
    },
    save: async (object: KeywordsDTO, id?: number | undefined | null): Promise<any> => {
        const formData = new FormData();
        formData.append("fileWithKeyword", object?.fileWithKeyword);
        formData.append("thematicId", object.thematicId.toString());
        formData.append("lang", object.lang);
        const { data } = await axios.request<Keyword[]>({
            headers: {
                "Content-Type": "multipart/form-data",
            },
            method: id ? "put" : "post",
            url: id ? `/keywords/${id}` : "/keywords",
            data: formData,
        });

        return data;
    },

    createAOrder: async (order: UpsertKeywordOrderText): Promise<Order> => {
        const response = await axios.request({
            method: "post",
            url: `/orders/createByKeyword`,
            data: toSnakeCase(order),
        });
        return OrderSchema.cast(toCamelCase(response.data), { stripUnknown: true });
    },

    updateMetrics: async (ids: number[], metrics: Array<string>): Promise<any> => {
        const response = await axios.request({
            method: "post",
            url: `/keywords/metrics/bulkUpdate?ids=${ids.join(",")}&metrics=${metrics.join(",")}`,
        });
        return response;
    },

    askJarvisForTitleSuggestion: async (
        thematicId: number | null = null,
        keyword: string,
        contentType: TContentType,
        orderId: number | null = null,
        rdId: number | null = null
    ): Promise<string[]> => {
        const { data } = await axios.request({
            method: "get",
            url: `/askJarvisForTitleSuggestion?${
                thematicId ? `thematicId=${thematicId}` : `rdId=${rdId}`
            }&keyword=${keyword}&contentType=${contentType}${orderId ? `&orderId=${orderId}` : ""}`,
        });
        return data;
    },

    askJarvisForPlanSuggestion: async (
        thematicId: number | null = null,
        keyword: string,
        contentType: TContentType,
        wordCount: number,
        title: string,
        orderId: number | null = null,
        rdId: number | null = null
    ): Promise<string[]> => {
        const { data } = await axios.request({
            method: "get",
            url: `/askJarvisForPlanSuggestion?${
                thematicId ? `thematicId=${thematicId}` : `&rdId=${rdId}`
            }&keyword=${keyword}&contentType=${contentType}&wordCount=${wordCount}&title=${title}${
                orderId ? `&orderId=${orderId}` : ""
            }`,
        });
        return data;
    },

    jarvisTitleSuggestion: async (orderId: number): Promise<{ suggestion: string[] }> => {
        const { data } = await axios.request({
            method: "get",
            url: `/jarvisTitleSuggestion?orderId=${orderId}`,
        });
        return data;
    },

    AskjarvisForCategories: async (
        thematicId: number,
        numberOfCategories: number,
        lang: TLangType
    ): Promise<string[]> => {
        const { data } = await axios.request({
            method: "get",
            url: `/askJarvisForCategories?thematicId=${thematicId}&numberOfCategories=${numberOfCategories}&lang=${lang}`,
        });
        return data;
    },

    AskjarvisForMT: async (
        thematicId: number,
        domain: string,
        lang: TLangType
    ): Promise<string> => {
        const { data } = await axios.request({
            method: "get",
            url: `/askJarvisForMT?thematicId=${thematicId}&domain=${domain}&lang=${lang}`,
        });
        return data;
    },

    jarvisSiloKeywordsSuggestion: async (keyword: string, lang: string): Promise<string[]> => {
        const { data } = await axios.request({
            method: "get",
            url: `/jarvisSiloKeywordsSuggestion?keyword=${keyword}&lang=${lang}`,
        });
        return data;
    },

    jarvisPlanSuggestion: async (orderId: string): Promise<{ suggestion: string[] }> => {
        const { data } = await axios.request({
            method: "get",
            url: `/jarvisPlanSuggestion?orderId=${orderId}`,
        });
        return data;
    },

    formTransformer: (object: Keyword): KeywordsDTO | null => {
        if (!object) {
            return null;
        }

        return null;
        /* return {
            thematicId: object.thematic.id,
            fileWithKeyword: [{ keyword: object.keyword, volume: object.volume, kd: object.kd }],
        };*/
    },

    formTransformerToOrderText: (): object => {
        return {
            writingType: undefined,
            title: undefined,
            contentType: undefined,
            rdId: undefined,
            plan: undefined,
            wordCount: undefined,
        };
    },

    getMetrics: () => {
        return ["URL", "TRAFFIC", "DA", "BL", "TF", "WORDS"];
    },
};
